import { useEffect } from "react";

import type { QueryClient } from "@tanstack/react-query";
import { hashKey, useQueryClient } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { getRuntimeConfig } from "../config";
import { queryKeys } from "./queryKeys";

// SSE consumer for `GET /v1/stream` (docs/04-api-contract.md §6;
// docs/09-frontend-surface.md §5). A `stream_message` (the reserved noun —
// never "Event") is the server->client delivery/replay unit. The four message
// types are handled verbatim; each invalidates the corresponding TanStack Query
// cache entries through the central query-key factory (list prefixes through a
// coalescing window — see below). `agent_id` is optional:
// an agent id opens that agent's stream; null opens the caller's team-wide
// visible scope (server-resolved in core — the SPA never sends
// organization_id).
export type StreamMessage = components["schemas"]["StreamMessage"];
export type AnswerCreatedPayload =
  components["schemas"]["AnswerCreatedPayload"];

export interface StreamSubscription {
  close: () => void;
}

const answerCreatedMessages = new EventTarget();

export function subscribeToAnswerCreated(
  listener: (payload: AnswerCreatedPayload) => void,
): () => void {
  const handle = (event: Event) => {
    listener((event as CustomEvent<AnswerCreatedPayload>).detail);
  };
  answerCreatedMessages.addEventListener("answer.created", handle);
  return () => {
    answerCreatedMessages.removeEventListener("answer.created", handle);
  };
}

// Coalescing window for LIST-prefix invalidations. A batch run emits
// `job.updated`/`transcript.created` continuously, and invalidating a list
// prefix on every one of them forces TanStack to re-walk every loaded page of
// that list — `isStaleByTime` short-circuits on `isInvalidated` before
// `staleTime` is ever consulted — so the cost grows with how much of the list
// the user has loaded. The first message in a burst arms the window and later
// ones fold into it: a burst costs one invalidation, and the list is never
// more than one window behind (a resetting debounce would instead starve the
// list for as long as the batch keeps emitting). Non-list keys (queue health,
// the Answer prefix behind an open transcript) stay immediate.
// ASSUMED(docs/09 §5 does not state an SSE invalidation coalescing window; 2s
// collapses batch-run bursts while keeping lists visibly live.)
const LIST_INVALIDATION_WINDOW_MS = 2_000;

interface ListInvalidator {
  // Drops every armed window without invalidating — the stream it belongs to
  // is gone.
  cancel: () => void;
  schedule: (
    invalidation: {
      predicate?: (query: { queryKey: readonly unknown[] }) => boolean;
      queryKey: readonly unknown[];
    },
    dedupeKey?: readonly unknown[],
  ) => void;
}

// Per-subscription, never module-level: the shell re-opens the stream on every
// scope change, and a window armed by a closed stream must not survive it.
function createListInvalidator(client: QueryClient): ListInvalidator {
  const armed = new Map<string, number>();
  return {
    cancel: () => {
      for (const handle of armed.values()) {
        window.clearTimeout(handle);
      }
      armed.clear();
    },
    schedule: (invalidation, dedupeKey = invalidation.queryKey) => {
      const id = hashKey(dedupeKey);
      if (armed.has(id)) {
        return;
      }
      armed.set(
        id,
        window.setTimeout(() => {
          armed.delete(id);
          void client.invalidateQueries(invalidation);
        }, LIST_INVALIDATION_WINDOW_MS),
      );
    },
  };
}

function answerListFilters(
  queryKey: readonly unknown[],
): Readonly<Record<string, unknown>> | null {
  const filters = queryKey[2];
  return queryKey[0] === "answers" &&
    queryKey[1] === "list" &&
    filters != null &&
    typeof filters === "object" &&
    !Array.isArray(filters)
    ? (filters as Readonly<Record<string, unknown>>)
    : null;
}

function shouldInvalidateAnswerList(
  queryKey: readonly unknown[],
  payload: AnswerCreatedPayload,
): boolean {
  const filters = answerListFilters(queryKey);
  if (filters == null) {
    return false;
  }
  return !(
    payload.source === "machine" &&
    filters.needs_review === true &&
    typeof filters.agent_id === "string" &&
    filters.agent_id !== payload.agent_id
  );
}

function agentFilter(
  agentId: string | null | undefined,
): { agent_id: string } | undefined {
  return agentId == null ? undefined : { agent_id: agentId };
}

function applyStreamMessage(
  message: StreamMessage,
  client: QueryClient,
  lists: ListInvalidator,
): void {
  const filter = agentFilter(message.agent_id);
  switch (message.message_type) {
    case "answer.created":
      lists.schedule({ queryKey: queryKeys.markers.reviewSummary({}) });
      lists.schedule({ queryKey: queryKeys.alignment.all });
      // Keep the open transcript fresh immediately, then coalesce broader
      // Answer-list refreshes. Runtime validation preserves compatibility
      // with a replayed pre-payload message: it still refreshes lists, but
      // cannot target a transcript it does not name.
      if (typeof message.payload.transcript_id === "string") {
        void client.invalidateQueries({
          queryKey: queryKeys.answers.list({
            transcript_id: message.payload.transcript_id,
          }),
        });
      }
      lists.schedule(
        {
          predicate: (query) =>
            shouldInvalidateAnswerList(query.queryKey, message.payload),
          queryKey: queryKeys.answers.all,
        },
        [
          ...queryKeys.answers.all,
          "stream-list",
          message.payload.source,
          message.payload.agent_id,
        ],
      );
      answerCreatedMessages.dispatchEvent(
        new CustomEvent("answer.created", { detail: message.payload }),
      );
      break;
    case "transcript.created":
      lists.schedule({
        queryKey: filter
          ? queryKeys.transcripts.list(filter)
          : queryKeys.transcripts.all,
      });
      break;
    case "job.updated":
      lists.schedule({ queryKey: queryKeys.transcripts.all });
      lists.schedule({ queryKey: queryKeys.batches.all });
      void client.invalidateQueries({ queryKey: queryKeys.queueHealth });
      break;
    case "batch.updated":
      lists.schedule({ queryKey: queryKeys.batches.all });
      break;
  }
}

export function subscribeToStream(
  agentId: string | null,
  client: QueryClient,
): StreamSubscription {
  // Authenticated by the session cookie (withCredentials); no separate token is
  // attached. The native EventSource re-sends the SSE `Last-Event-ID` header on
  // reconnect, so the server replays from the last delivered message id (the
  // resume cursor) — the client does not manage the cursor by hand.
  const query =
    agentId == null
      ? ""
      : `?${new URLSearchParams({ agent_id: agentId }).toString()}`;
  // Reach the API via the same injected base the generated REST client uses
  // (apiClient.ts: getRuntimeConfig().apiBaseUrl); "" = same-origin. Keeps the
  // SSE channel and REST calls on one config-driven base so cross-origin
  // deployments work (docs/09-frontend-surface.md §3/§8, docs/08 runtime config).
  const { apiBaseUrl } = getRuntimeConfig();
  const source = new EventSource(`${apiBaseUrl}/v1/stream${query}`, {
    withCredentials: true,
  });

  // At-least-once delivery: replayed messages are applied idempotently BY KEY
  // (the message id), never by arrival count.
  const applied = new Set<string>();
  const lists = createListInvalidator(client);

  source.addEventListener("message", (event: MessageEvent) => {
    const message = JSON.parse(event.data as string) as StreamMessage;
    if (applied.has(message.id)) {
      return;
    }
    applied.add(message.id);
    applyStreamMessage(message, client, lists);
  });

  return {
    close: () => {
      lists.cancel();
      source.close();
    },
  };
}

// The shell's single app-wide stream subscription (docs/09 §5): it follows
// the sidebar agent selector — a selected agent opens the agent-scoped
// stream, "All agents" (null) opens the team-wide stream (#684) — and
// re-opens on scope change. `enabled` gates on a confirmed session so the
// login handoff never spins an unauthenticated EventSource retry loop.
export function useShellStream(agentId: string | null, enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const subscription = subscribeToStream(agentId, queryClient);
    return () => {
      subscription.close();
    };
  }, [agentId, enabled, queryClient]);
}
